import {inject, Injectable} from '@angular/core';
import {FieldParameters} from "./definitions/field-parameters";
import {SearchParameters} from "./definitions/search-parameters";
import {
  SearchSelectorItem
} from "../object-edit/edit-field-search-selector-multiple/edit-field-search-selector-multiple.component";
import {BaseModel} from "./definitions/base-model";
import {FieldMetaService} from "./field-meta.service";
import {SearchReferenceService} from "./search-reference.service";
import {LoggerService} from "./logger.service";
import {OptionsDialogService} from "../object-edit/options-dialog.service";
import {ObjectDeletionService} from "./object-deletion.service";
import {CrudService} from "./crud.service";
import {InlineArrayItemService} from "./inline-array-item.service";
import {FieldValueService} from "./field-value.service";
import {SolrFilterService} from "./solr-filter.service";
import {SearchService} from "./search.service";
import {EditFieldSearchSelectorContainer} from "./definitions/edit-field-search-selector-container";
import {FieldInputType} from "./definitions/field-input-type.enum";
import {AbstractControl} from "@angular/forms";

@Injectable({
  providedIn: 'root',
})
export class EditFieldSearchSelectorHandler {
  private readonly fieldMetaSvc: FieldMetaService = inject(FieldMetaService);
  private readonly searchReferenceService: SearchReferenceService = inject(SearchReferenceService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly optionsDialogService: OptionsDialogService = inject(OptionsDialogService);
  private readonly objectDeletionService: ObjectDeletionService = inject(ObjectDeletionService);
  private readonly crud: CrudService = inject(CrudService);
  private readonly inlineArrayItemService: InlineArrayItemService = inject(InlineArrayItemService);
  private readonly fieldValueSvc: FieldValueService = inject(FieldValueService);
  private readonly solrFilterService: SolrFilterService = inject(SolrFilterService);
  private readonly searchService: SearchService = inject(SearchService);


  initialize(fieldParameters: FieldParameters, formControlName: string): EditFieldSearchSelectorContainer {
    const editFieldSearchSelectorContainer = {} as EditFieldSearchSelectorContainer;

    editFieldSearchSelectorContainer.fieldParameters = fieldParameters;
    editFieldSearchSelectorContainer.formControlName = formControlName;
    editFieldSearchSelectorContainer.sectionsContainer = fieldParameters.sectionsContainer;
    editFieldSearchSelectorContainer.field = fieldParameters.field;
    editFieldSearchSelectorContainer.object = fieldParameters.object;
    editFieldSearchSelectorContainer.items = [];
    editFieldSearchSelectorContainer.itemsSet = false;
    let meta = this.fieldMetaSvc.getFieldMetaData(fieldParameters.object, fieldParameters.field);
    if (!meta || !Object.keys(meta).length) {
      meta = fieldParameters.field;
    }
    editFieldSearchSelectorContainer.reference = this.searchReferenceService.getSearchReferenceFromField(meta);
    return editFieldSearchSelectorContainer;
  }

  openCreateNew(editFieldSearchSelectorContainer: EditFieldSearchSelectorContainer) {
    const fieldParameters = new FieldParameters();
    fieldParameters.field = editFieldSearchSelectorContainer.fieldParameters.field;
    this.editItem(editFieldSearchSelectorContainer).then(art => {
      if (art) {
        const objects = Array.isArray(art) ? art : [art];
        this.checkAddNewItems(editFieldSearchSelectorContainer, objects).then();
      }
    }, reason => {
      if (reason) {
        this.logger.warn('Error storing new artifact: ' + reason.toLocaleString());
      }
    });
  }

  editItem(editFieldSearchSelectorContainer: EditFieldSearchSelectorContainer, item?: SearchSelectorItem): Promise<BaseModel> {
    return new Promise<any>(resolve => {
      if ((!item || editFieldSearchSelectorContainer.reference.can_edit_existing || editFieldSearchSelectorContainer.reference.can_edit_context) &&
        !editFieldSearchSelectorContainer.sectionsContainer.isCopy) {
        const fieldParameters: FieldParameters = new FieldParameters();
        fieldParameters.field = editFieldSearchSelectorContainer.field;
        fieldParameters.sectionsContainer = editFieldSearchSelectorContainer.sectionsContainer;
        if (item) {
          if (editFieldSearchSelectorContainer.reference.can_edit_context || item.artifact_id === item.$$object.artifact_id) {
            // The last part of the "if" will make sure editing existing publish events works
            fieldParameters.object = item;
          } else {
            fieldParameters.object = item.$$object;
          }
        }
        fieldParameters.sectionsContainer.isSecondDialog = fieldParameters.sectionsContainer.isDialog;
        this.optionsDialogService.createOption(fieldParameters).then(
          option => {
            resolve(option)
          },
          reason => {
            if (reason) {
              this.logger.error(`Create option failed: ${reason}`);
            }
          });
      }
    });
  }

  async checkAddNewItems(editFieldSearchSelectorContainer: EditFieldSearchSelectorContainer, objects: Array<BaseModel>) {
    const refProp = editFieldSearchSelectorContainer.reference.ref_prop;
    if (!refProp) {
      this.logger.error('An reference prop must be defined for field ' + editFieldSearchSelectorContainer.field.name);
      return;
    }
    for (const obj of objects) {
      let targetData: any;
      const existed = this.checkIdExistedRemoveDestroyFlag(editFieldSearchSelectorContainer.items, obj.artifact_id, refProp);
      if (existed) {
        continue;
      }
      if (obj.meta_type === 'adm_event') {
        targetData = {
          context_id: editFieldSearchSelectorContainer.object.artifact_id,
          artifact_id: obj.artifact_id,
          artifact_id_value: obj.artifact_name,
          superobject_type_id: obj.superobject_type_id,
          object_type: 'SuperobjectAdmEventItem'
        };
      } else {
        targetData = {
          context_object_type: obj.object_type,
          context_id: editFieldSearchSelectorContainer.sectionsContainer.rootObject.artifact_id || editFieldSearchSelectorContainer.sectionsContainer.rootObject.context_id
        };
        targetData[refProp] = obj.artifact_id;
      }
      targetData[refProp + '_value'] = obj.artifact_name;
      if (editFieldSearchSelectorContainer.field.input_type === FieldInputType.SEARCH_SELECTOR_MULTIPLE) {
        // Unfortunately, adding admin events causes the server to create an extra admin event seemingly without any
        // connection to the one added here so marking form as dirty instead
        if (obj.meta_type !== 'adm_event') {
          await this.inlineArrayItemService.addInlineArrayItemToForm(editFieldSearchSelectorContainer.fieldParameters, false, targetData);
        } else {
          this.fieldValueSvc.markAsDirty(editFieldSearchSelectorContainer.sectionsContainer.formGroup, editFieldSearchSelectorContainer.formControlName);
        }
      } else {
        const artifactId = targetData[refProp];
        const textValue = targetData[refProp + '_value'];
        this.setSingleValue(editFieldSearchSelectorContainer, artifactId, textValue)
      }
    }
    await this.setItems(editFieldSearchSelectorContainer);
  }

  setSingleValue(editFieldSearchSelectorContainer: EditFieldSearchSelectorContainer, artifactId: string, textValue: string) {
    const fieldParameters = editFieldSearchSelectorContainer.fieldParameters;
    this.fieldValueSvc.setFieldValue(fieldParameters.object, fieldParameters.field.name, artifactId);
    this.fieldValueSvc.setFieldValue(fieldParameters.object, fieldParameters.field.name, textValue, true);
    const control: AbstractControl = fieldParameters.sectionsContainer.formGroup.controls[editFieldSearchSelectorContainer.formControlName];
    if (control) {
      control.setValue(textValue);
      control.markAsDirty();
    } else {
      console.error('Control not found: ' + editFieldSearchSelectorContainer.formControlName);
    }
  }

  async getItemsFromSearch(editFieldSearchSelectorContainer: EditFieldSearchSelectorContainer): Promise<any[]> {
    let res = [];
    const parentField = editFieldSearchSelectorContainer.reference.parent_field || 'artifact_id';
    const parentTargetField = editFieldSearchSelectorContainer.reference.parent_target_field;
    if (parentTargetField) {
      const params = {} as SearchParameters;
      const parentId = editFieldSearchSelectorContainer.object[parentField];
      this.solrFilterService.addFq(params, parentTargetField, parentId);
      this.solrFilterService.addFq(params, 'object_type', editFieldSearchSelectorContainer.reference.object_type);
      if (editFieldSearchSelectorContainer.reference.sort) {
        params.sort = editFieldSearchSelectorContainer.reference.sort;
      }
      const searchResult = await this.searchService.search(params);
      // Result is reversed as this would be the expected order when obtaining elements from array
      res = [...searchResult.artifacts].reverse();
    } else {
      this.logger.warn('A parent target field has not been specified in reference');
    }
    return res;
  }

  getItem(editFieldSearchSelectorContainer: EditFieldSearchSelectorContainer, item: BaseModel, refProp: string, index: number) {
    const itemObject = new BaseModel(item[refProp]);
    itemObject.artifact_name = item[refProp + '_value'] || item.artifact_name || item[refProp];
    itemObject.object_type = editFieldSearchSelectorContainer.reference.object_type;
    item.$$object = itemObject;
    item.$$index = index;
    return item;
  }

  async setItemsAndDeletable(editFieldSearchSelectorContainer: EditFieldSearchSelectorContainer) {
    await this.setItems(editFieldSearchSelectorContainer);
    await this.objectDeletionService.setObjectsDeletable(editFieldSearchSelectorContainer.sectionsContainer.rootObject, editFieldSearchSelectorContainer.items);
  }

  async setItems(editFieldSearchSelectorContainer: EditFieldSearchSelectorContainer) {
    const arrayName = editFieldSearchSelectorContainer.field.name;
    let array = editFieldSearchSelectorContainer.object[arrayName];
    if (editFieldSearchSelectorContainer.reference.get_existing_items_from_search) {
      array = await this.getItemsFromSearch(editFieldSearchSelectorContainer);
    }
    editFieldSearchSelectorContainer.items = [];
    if (!editFieldSearchSelectorContainer.reference.ref_prop) {
      this.logger.error('An reference prop must be defined for field ' + editFieldSearchSelectorContainer.field.name);
    } else {
      if (array.length) {
        editFieldSearchSelectorContainer.items = array.map((item: any, index: number) => this.getItem(editFieldSearchSelectorContainer, item, editFieldSearchSelectorContainer.reference.ref_prop, index));
      }
    }
    editFieldSearchSelectorContainer.itemsSet = true;
  }

  private checkIdExistedRemoveDestroyFlag(items: SearchSelectorItem[], artifactId: string, refProp: string) {
    let existed = false
    for (const existing of items) {
      if (artifactId === existing[refProp]) {
        existed = true;
        this.crud.deleteDestroy(existing);
      }
    }
    return existed;
  }

}
